/**
 * The promotion screen by protocol. A list of protocols, paged and searchable, each with the bar of where
 * its scenario types stand; open one to generate templates for its ready types and promote them. The
 * list does not grow with intents or templates, only with protocols, and only one page is read at a time.
 */

import { useDeferredValue, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/input';
import { useProtocols } from '@/hooks/use-promotion';
import { ProtocolPanel } from './protocol-panel';
import { ProtocolRow } from './protocol-row';

export const PAGE_SIZE = 20;

interface ProtocolsViewProps {
	onOpenBlueprint: (blueprintId: string) => void;
	/** The protocol to open first, as a link from back testing names it. */
	initialSelected?: string | null;
}

export const ProtocolsView = ({ onOpenBlueprint, initialSelected = null }: ProtocolsViewProps) => {
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(1);
	const [selected, setSelected] = useState<string | null>(initialSelected);
	const deferredSearch = useDeferredValue(search.trim());
	const { data, isLoading, isFetching } = useProtocols({ search: deferredSearch || undefined, page, limit: PAGE_SIZE });

	const protocols = data?.protocols ?? [];
	const total = data?.total ?? 0;
	const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

	return (
		<div className='grid min-h-0 flex-1 grid-cols-[minmax(260px,38%)_minmax(0,1fr)] rounded-md border'>
			<div className='flex min-h-0 min-w-0 flex-col border-r border-gray-500'>
				<div className='shrink-0 border-b border-gray-500 p-3'>
					<SearchInput
						value={search}
						onChange={(event) => {
							setSearch(event.target.value);
							setPage(1);
						}}
						placeholder='Search protocols'
						className='h-7 w-full text-xs'
					/>
				</div>
				<div className='min-h-0 flex-1 overflow-y-auto'>
					{isLoading ? (
						<p className='flex items-center gap-2 px-4 py-3 text-xs text-gray-800'>
							<Loader2 className='size-3.5 animate-spin' /> Loading protocols…
						</p>
					) : protocols.length === 0 ? (
						<p className='px-4 py-3 text-xs leading-4 text-gray-800'>
							{deferredSearch
								? 'No protocol matches that search.'
								: 'No protocol has scenario types yet. Generate them from a protocol: ⋯ → Promote scenarios.'}
						</p>
					) : (
						protocols.map((protocol) => (
							<ProtocolRow key={protocol.sopId} protocol={protocol} selected={selected === protocol.sopId} onSelect={setSelected} />
						))
					)}
				</div>
				{total > PAGE_SIZE && (
					<div className='flex shrink-0 items-center justify-between border-t border-gray-500 px-3 py-2 text-xs'>
						<Button variant='ghost' size='sm' disabled={page <= 1 || isFetching} onClick={() => setPage(page - 1)}>
							Previous
						</Button>
						<span className='tabular-nums text-gray-800'>
							{page} of {pages}
						</span>
						<Button variant='ghost' size='sm' disabled={page >= pages || isFetching} onClick={() => setPage(page + 1)}>
							Next
						</Button>
					</div>
				)}
			</div>
			<div className='flex min-h-0 min-w-0 flex-col'>
				{selected ? (
					<ProtocolPanel key={selected} sopId={selected} onOpenBlueprint={onOpenBlueprint} />
				) : (
					<p className='p-4 text-xs leading-4 text-gray-800'>
						Pick a protocol to see where each of its scenario types stands. Generating, testing and promoting run from
						the protocol itself.
					</p>
				)}
			</div>
		</div>
	);
};
